import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  FileSpreadsheet,
  Download,
  Check,
  X,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import {
  rateBulkImportApi,
  RateBulkImport,
  ImportTemplateFormat,
  ImportStatus,
  TransportMode,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { cn } from '@/lib/utils';

// Wizard step types
type WizardStep = 'configure' | 'upload' | 'review' | 'processing' | 'complete';

const STEPS: { key: WizardStep; label: string }[] = [
  { key: 'configure', label: 'Configure' },
  { key: 'upload', label: 'Upload' },
  { key: 'review', label: 'Review' },
  { key: 'processing', label: 'Processing' },
  { key: 'complete', label: 'Complete' },
];

const TEMPLATE_FORMATS: { value: ImportTemplateFormat; label: string; description: string }[] = [
  { value: 'Default', label: 'Default', description: 'Standard rate import format' },
  { value: 'Shipwaves', label: 'Shipwaves', description: 'Shipwaves platform format' },
  { value: 'Custom', label: 'Custom', description: 'Custom mapped format' },
];

const TRANSPORT_MODES: { value: TransportMode; label: string }[] = [
  { value: 'TruckFTL', label: 'Truck FTL' },
  { value: 'TruckLTL', label: 'Truck LTL' },
  { value: 'Ocean', label: 'Ocean' },
  { value: 'Air', label: 'Air' },
  { value: 'Rail', label: 'Rail' },
  { value: 'Intermodal', label: 'Intermodal' },
];

const STATUS_COLORS: Record<ImportStatus, string> = {
  Pending: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  Uploading: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  Processing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  Completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  Failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  PartialSuccess: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  Cancelled: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
};

export default function RateBulkImportPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('configure');
  const [templateFormat, setTemplateFormat] = useState<ImportTemplateFormat>('Default');
  const [transportMode, setTransportMode] = useState<TransportMode | undefined>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importRecord, setImportRecord] = useState<RateBulkImport | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  // History state
  const [history, setHistory] = useState<RateBulkImport[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Polling for status updates
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load import history
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const response = await rateBulkImportApi.getHistory({ limit: 10 });
      setHistory(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Poll for status when processing
  useEffect(() => {
    if (currentStep === 'processing' && importRecord) {
      pollIntervalRef.current = setInterval(async () => {
        try {
          const status = await rateBulkImportApi.getStatus(importRecord.id);
          setImportRecord(status);

          if (status.status === 'Completed' || status.status === 'Failed' || status.status === 'PartialSuccess') {
            setCurrentStep('complete');
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
            }
          }
        } catch (error) {
          console.error('Failed to poll status:', error);
        }
      }, 2000);

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [currentStep, importRecord]);

  // Handlers
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const blob = await rateBulkImportApi.downloadTemplate(templateFormat, transportMode);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rate_template_${templateFormat.toLowerCase()}${transportMode ? `_${transportMode.toLowerCase()}` : ''}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleFileSelect = (file: File) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload an Excel or CSV file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }
    setSelectedFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleStartImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setLoading(true);
    try {
      // Step 1: Create import record
      const record = await rateBulkImportApi.create({
        templateFormat,
        mode: transportMode,
      });
      setImportRecord(record);

      // Step 2: Upload file
      const updatedRecord = await rateBulkImportApi.uploadFile(record.id, selectedFile);
      setImportRecord(updatedRecord);
      setCurrentStep('review');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleProcessImport = async () => {
    if (!importRecord) return;

    setLoading(true);
    try {
      const updated = await rateBulkImportApi.processImport(importRecord.id);
      setImportRecord(updated);
      setCurrentStep('processing');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelImport = async () => {
    if (!importRecord) return;

    try {
      await rateBulkImportApi.cancel(importRecord.id);
      toast.success('Import cancelled');
      resetWizard();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const resetWizard = () => {
    setCurrentStep('configure');
    setSelectedFile(null);
    setImportRecord(null);
    loadHistory();
  };

  const getStepIndex = (step: WizardStep) => STEPS.findIndex((s) => s.key === step);
  const currentStepIndex = getStepIndex(currentStep);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Rate Import"
        description="Bulk import rates from spreadsheet files"
        actions={
          <Button variant="outline" onClick={() => navigate('/tms/rates')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Rates
          </Button>
        }
      />

      {/* Wizard Progress */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-8">
            {STEPS.map((step, idx) => (
              <div key={step.key} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-colors',
                      idx < currentStepIndex
                        ? 'bg-emerald-500 text-white'
                        : idx === currentStepIndex
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {idx < currentStepIndex ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      idx + 1
                    )}
                  </div>
                  <span className="text-xs mt-2 text-center font-medium">{step.label}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div
                    className={cn(
                      'h-0.5 flex-1 mx-2',
                      idx < currentStepIndex ? 'bg-emerald-500' : 'bg-muted'
                    )}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step Content */}
          <div className="min-h-[300px]">
            {/* Step 1: Configure */}
            {currentStep === 'configure' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Template Format</Label>
                    <Select
                      value={templateFormat}
                      onValueChange={(v) => setTemplateFormat(v as ImportTemplateFormat)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TEMPLATE_FORMATS.map((f) => (
                          <SelectItem key={f.value} value={f.value}>
                            <div>
                              <div className="font-medium">{f.label}</div>
                              <div className="text-xs text-muted-foreground">{f.description}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Transport Mode (Optional)</Label>
                    <Select
                      value={transportMode || ''}
                      onValueChange={(v) => setTransportMode(v as TransportMode || undefined)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All Modes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">All Modes</SelectItem>
                        {TRANSPORT_MODES.map((m) => (
                          <SelectItem key={m.value} value={m.value}>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="p-4 bg-muted/30 rounded-lg border border-dashed">
                  <div className="flex items-center gap-4">
                    <FileSpreadsheet className="w-8 h-8 text-emerald-500" />
                    <div className="flex-1">
                      <h4 className="font-medium">Download Template</h4>
                      <p className="text-sm text-muted-foreground">
                        Get the template file to prepare your rate data
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleDownloadTemplate}
                      disabled={downloadingTemplate}
                    >
                      {downloadingTemplate ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Download
                    </Button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={() => setCurrentStep('upload')}>
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Upload */}
            {currentStep === 'upload' && (
              <div className="space-y-6">
                <div
                  className={cn(
                    'border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer',
                    selectedFile
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/10'
                      : 'border-muted-foreground/30 hover:border-primary'
                  )}
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileSelect(file);
                    }}
                  />

                  {selectedFile ? (
                    <div className="space-y-2">
                      <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto" />
                      <div className="font-medium">{selectedFile.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {formatFileSize(selectedFile.size)}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                        }}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-12 h-12 text-muted-foreground mx-auto" />
                      <div className="font-medium">Drop your file here or click to browse</div>
                      <div className="text-sm text-muted-foreground">
                        Supports .xlsx, .xls, .csv (max 10MB)
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setCurrentStep('configure')}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button onClick={handleStartImport} disabled={!selectedFile || loading}>
                    {loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Upload & Validate
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Review */}
            {currentStep === 'review' && importRecord && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-muted/30 rounded-lg text-center">
                    <div className="text-2xl font-bold">{importRecord.totalRows}</div>
                    <div className="text-sm text-muted-foreground">Total Rows</div>
                  </div>
                  <div className="p-4 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg text-center">
                    <div className="text-2xl font-bold text-emerald-600">
                      {importRecord.totalRows - importRecord.failedRows}
                    </div>
                    <div className="text-sm text-muted-foreground">Valid</div>
                  </div>
                  <div className="p-4 bg-red-100 dark:bg-red-900/20 rounded-lg text-center">
                    <div className="text-2xl font-bold text-red-600">{importRecord.failedRows}</div>
                    <div className="text-sm text-muted-foreground">Errors</div>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-lg text-center">
                    <div className="text-2xl font-bold">{templateFormat}</div>
                    <div className="text-sm text-muted-foreground">Format</div>
                  </div>
                </div>

                {importRecord.errors && importRecord.errors.length > 0 && (
                  <div className="space-y-2">
                    <div className="font-medium text-red-600 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Validation Errors ({importRecord.errors.length})
                    </div>
                    <div className="max-h-48 overflow-y-auto border rounded-lg">
                      {importRecord.errors.slice(0, 20).map((error, idx) => (
                        <div
                          key={idx}
                          className="px-4 py-2 text-sm border-b last:border-b-0 flex items-start gap-2"
                        >
                          <Badge variant="outline" className="shrink-0">
                            Row {error.row}
                          </Badge>
                          <span className="text-muted-foreground">
                            {error.field && <span className="font-medium">{error.field}: </span>}
                            {error.message}
                          </span>
                        </div>
                      ))}
                      {importRecord.errors.length > 20 && (
                        <div className="px-4 py-2 text-sm text-muted-foreground text-center">
                          And {importRecord.errors.length - 20} more errors...
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-between">
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleCancelImport}>
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedFile(null);
                        setImportRecord(null);
                        setCurrentStep('upload');
                      }}
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Upload Different File
                    </Button>
                  </div>
                  <Button
                    onClick={handleProcessImport}
                    disabled={loading || importRecord.totalRows === importRecord.failedRows}
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Process Import
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Processing */}
            {currentStep === 'processing' && importRecord && (
              <div className="space-y-6 text-center py-8">
                <Loader2 className="w-16 h-16 text-primary mx-auto animate-spin" />
                <div>
                  <h3 className="text-lg font-medium">Processing Import</h3>
                  <p className="text-muted-foreground">Please wait while we import your rates...</p>
                </div>

                <div className="max-w-md mx-auto space-y-2">
                  <Progress
                    value={
                      importRecord.totalRows > 0
                        ? (importRecord.processedRows / importRecord.totalRows) * 100
                        : 0
                    }
                  />
                  <div className="text-sm text-muted-foreground">
                    {importRecord.processedRows} / {importRecord.totalRows} rows processed
                  </div>
                </div>

                <Button variant="outline" onClick={handleCancelImport}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel Import
                </Button>
              </div>
            )}

            {/* Step 5: Complete */}
            {currentStep === 'complete' && importRecord && (
              <div className="space-y-6 text-center py-8">
                {importRecord.status === 'Completed' ? (
                  <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto" />
                ) : importRecord.status === 'PartialSuccess' ? (
                  <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto" />
                ) : (
                  <XCircle className="w-16 h-16 text-red-500 mx-auto" />
                )}

                <div>
                  <h3 className="text-lg font-medium">
                    {importRecord.status === 'Completed'
                      ? 'Import Completed!'
                      : importRecord.status === 'PartialSuccess'
                        ? 'Import Partially Completed'
                        : 'Import Failed'}
                  </h3>
                  <p className="text-muted-foreground">
                    {importRecord.status === 'Completed'
                      ? 'All rates have been successfully imported.'
                      : importRecord.status === 'PartialSuccess'
                        ? 'Some rates could not be imported due to errors.'
                        : 'The import failed. Please review the errors and try again.'}
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <div className="text-2xl font-bold">{importRecord.totalRows}</div>
                    <div className="text-xs text-muted-foreground">Total</div>
                  </div>
                  <div className="p-4 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-emerald-600">
                      {importRecord.successRows}
                    </div>
                    <div className="text-xs text-muted-foreground">Success</div>
                  </div>
                  <div className="p-4 bg-red-100 dark:bg-red-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{importRecord.failedRows}</div>
                    <div className="text-xs text-muted-foreground">Failed</div>
                  </div>
                </div>

                {importRecord.errors && importRecord.errors.length > 0 && (
                  <div className="max-w-lg mx-auto text-left">
                    <div className="font-medium text-red-600 mb-2">Errors:</div>
                    <div className="max-h-32 overflow-y-auto border rounded-lg text-sm">
                      {importRecord.errors.slice(0, 10).map((error, idx) => (
                        <div key={idx} className="px-3 py-2 border-b last:border-b-0">
                          Row {error.row}: {error.message}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-center gap-4">
                  <Button variant="outline" onClick={resetWizard}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Import More
                  </Button>
                  <Button onClick={() => navigate('/tms/rates')}>
                    <FileText className="w-4 h-4 mr-2" />
                    View Rates
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Import History */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Recent Imports
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={loadHistory}>
            <RefreshCw className={cn('w-4 h-4', loadingHistory && 'animate-spin')} />
          </Button>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : history.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-muted/50 text-left">
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      File
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Format
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                      Rows
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                      Success
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {history.map((record) => (
                    <tr key={record.id} className="hover:bg-muted/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-emerald-500" />
                          <span className="font-medium text-sm truncate max-w-[200px]">
                            {record.fileName}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline">{record.templateFormat}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">{record.totalRows}</td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-emerald-600">{record.successRows}</span>
                        {record.failedRows > 0 && (
                          <span className="text-red-600 ml-1">/ {record.failedRows} failed</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={STATUS_COLORS[record.status]}>{record.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {new Date(record.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No import history yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
